import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Maximize, Volume2, VolumeX, Cast, Airplay } from "lucide-react";
import { toast } from "sonner";
import AdRoll from "./AdRoll";
import StreamWatermark from "./StreamWatermark";
import { useAuth } from "../context/AuthContext";
import useVideoPreroll from "../lib/useVideoPreroll";

// Subscribers with these tiers skip pre-roll ads
const AD_FREE_TIERS = new Set(["premium_viewer", "creator", "premium_creator"]);

function shouldShowAds(user) {
  if (!user) return true; // unauthenticated → ads
  if (user.role === "admin") return false;
  if (AD_FREE_TIERS.has(user.subscription_tier)) return false;
  return true; // free + basic_viewer → ads
}

function EmbedPlayer({ embedUrl, sourceType, watermark }) {
  return (
    <div data-testid="video-player-embed" className="relative w-full aspect-video bg-black rounded-xl overflow-hidden">
      <iframe
        data-testid="video-iframe"
        src={embedUrl}
        title="External video"
        className="w-full h-full"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
      {sourceType && (
        <div className="absolute top-3 left-3 bg-black/70 backdrop-blur text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full" data-testid="embed-source-badge">
          From {sourceType}
        </div>
      )}
      {watermark && <Watermark variant={watermark} />}
      {watermark && <StreamWatermark variant={watermark} size="sm" />}
    </div>
  );
}

function Watermark({ variant }) {
  // variant: "main" uses Tap IN TV logo; "kids" uses Kids Network logo
  const src = variant === "kids" ? "/tapintv-kids-logo.png" : "/tapintv-logo-v2.png";
  const size = variant === "kids" ? "w-14 sm:w-20 md:w-24" : "w-12 sm:w-16 md:w-20";
  return (
    <img
      src={src}
      alt=""
      data-testid={`live-watermark-${variant}`}
      aria-hidden="true"
      draggable="false"
      className={`pointer-events-none select-none absolute top-3 right-3 ${size} opacity-70 drop-shadow-[0_0_12px_rgba(0,0,0,0.6)]`}
    />
  );
}

export default function VideoPlayer({ embedUrl, sourceType, watermark, skipAds = false, videoId, ...rest }) {
  const { user } = useAuth();
  const [adDone, setAdDone] = useState(false);
  const [prerollChecked, setPrerollChecked] = useState(false);
  const [prerollPlayed, setPrerollPlayed] = useState(false);
  const wantsAds = !skipAds && shouldShowAds(user);
  const hostVideoElRef = useRef(null);
  const { adContainerRef, startPreroll } = useVideoPreroll({ videoId, placement: "preroll", enabled: wantsAds });

  useEffect(() => {
    // Reset ad gate if the source changes (e.g., user navigates between videos)
    setAdDone(false);
    setPrerollChecked(false);
    setPrerollPlayed(false);
  }, [embedUrl, rest?.src]);

  // Try IMA/VAST preroll first. If it plays, skip AdRoll fallback.
  useEffect(() => {
    if (!wantsAds || prerollChecked || embedUrl) { setPrerollChecked(true); return; }
    // Wait a tick so the host <video> element is mounted
    const t = setTimeout(async () => {
      const hostEl = hostVideoElRef.current;
      if (!hostEl) { setPrerollChecked(true); return; }
      const res = await startPreroll(hostEl);
      if (res?.played) { setPrerollPlayed(true); setAdDone(true); }
      setPrerollChecked(true);
    }, 200);
    return () => clearTimeout(t);
  }, [wantsAds, embedUrl, prerollChecked, startPreroll]);

  const content = embedUrl
    ? <EmbedPlayer embedUrl={embedUrl} sourceType={sourceType} watermark={watermark} />
    : <NativePlayer watermark={watermark} videoElRef={hostVideoElRef} {...rest} />;

  if (!wantsAds || adDone) return (
    <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden" data-testid="video-player-shell">
      {content}
      {/* IMA container is always rendered (hidden when no ad) so the SDK can anchor here */}
      <div ref={adContainerRef} data-testid="ima-ad-container" className={`absolute inset-0 z-20 ${prerollPlayed ? "hidden" : "pointer-events-none"}`} />
    </div>
  );

  // Waiting on preroll check → show shell; AdRoll fallback triggers only after IMA says no-fill
  return (
    <div data-testid="video-player-with-ads" className="relative w-full aspect-video bg-black rounded-xl overflow-hidden">
      {content}
      <div ref={adContainerRef} data-testid="ima-ad-container" className="absolute inset-0 z-20" />
      {prerollChecked && !prerollPlayed && (
        <AdRoll enabled={true} onComplete={() => setAdDone(true)} />
      )}
    </div>
  );
}

function NativePlayer({ src, poster, onProgress, startAt = 0, onEnded, vtt, watermark, videoElRef }) {
  const videoRef = useRef(null);
  // Keep external ref in sync so IMA SDK can anchor to our <video> element
  useEffect(() => { if (videoElRef) videoElRef.current = videoRef.current; }, [videoElRef]);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hide, setHide] = useState(false);
  const [castAvailable, setCastAvailable] = useState(false);
  const [airplayAvailable, setAirplayAvailable] = useState(false);
  const [casting, setCasting] = useState(false);
  const hideTimer = useRef(null);

  useEffect(() => {
    if (videoRef.current && startAt > 0) {
      videoRef.current.currentTime = startAt;
    }
  }, [startAt]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.remote && typeof v.remote.watchAvailability === "function") {
      let cancel;
      v.remote
        .watchAvailability((available) => setCastAvailable(!!available))
        .then((id) => { cancel = id; })
        .catch(() => setCastAvailable(false));
      return () => {
        if (cancel != null && v.remote?.cancelWatchAvailability) {
          try { v.remote.cancelWatchAvailability(cancel); } catch {}
        }
      };
    }
  }, [src]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (window.WebKitPlaybackTargetAvailabilityEvent) {
      const onAvail = (e) => setAirplayAvailable(e.availability === "available");
      v.addEventListener("webkitplaybacktargetavailabilitychanged", onAvail);
      return () => v.removeEventListener("webkitplaybacktargetavailabilitychanged", onAvail);
    }
  }, [src]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v?.remote) return;
    const onChange = () => setCasting(v.remote.state === "connected" || v.remote.state === "connecting");
    v.remote.addEventListener("connect", onChange);
    v.remote.addEventListener("connecting", onChange);
    v.remote.addEventListener("disconnect", onChange);
    return () => {
      v.remote.removeEventListener("connect", onChange);
      v.remote.removeEventListener("connecting", onChange);
      v.remote.removeEventListener("disconnect", onChange);
    };
  }, [src]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const onTime = () => {
    const v = videoRef.current;
    if (!v) return;
    setProgress(v.currentTime);
    setDuration(v.duration || 0);
    if (onProgress) onProgress(v.currentTime, v.duration || 0);
  };

  const seek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    v.currentTime = pct * duration;
  };

  const full = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitEnterFullscreen) el.webkitEnterFullscreen();
  };

  // Keyboard shortcuts: k/space play, j/l seek ±10s, f fullscreen, m mute, 0-9 jump
  useEffect(() => {
    const onKey = (e) => {
      const v = videoRef.current;
      if (!v) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      if (e.key === "k" || e.key === " ") { e.preventDefault(); toggle(); }
      else if (e.key === "j") { v.currentTime = Math.max(0, v.currentTime - 10); }
      else if (e.key === "l") { v.currentTime = Math.min(v.duration || 0, v.currentTime + 10); }
      else if (e.key === "f") { full(); }
      else if (e.key === "m") { v.muted = !v.muted; setMuted(v.muted); }
      else if (/^[0-9]$/.test(e.key)) { if (v.duration) v.currentTime = (parseInt(e.key, 10) / 10) * v.duration; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showControls = () => {
    setHide(false);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setHide(true), 3000);
  };

  const castToTV = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (v.remote && typeof v.remote.prompt === "function") {
        await v.remote.prompt();
        return;
      }
      toast.error("Casting not supported in this browser");
    } catch (e) {
      if (e?.name !== "NotAllowedError" && e?.name !== "AbortError") {
        toast.error("Couldn't connect to a TV");
      }
    }
  };

  const airplayToTV = () => {
    const v = videoRef.current;
    if (!v) return;
    if (typeof v.webkitShowPlaybackTargetPicker === "function") {
      v.webkitShowPlaybackTargetPicker();
    } else {
      toast.error("AirPlay not supported");
    }
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div
      data-testid="video-player"
      className="relative w-full aspect-video bg-black rounded-xl overflow-hidden group"
      onMouseMove={showControls}
      onMouseLeave={() => setHide(true)}
    >
      <video
        ref={videoRef}
        data-testid="video-element"
        src={src}
        poster={poster}
        className="w-full h-full object-contain bg-black"
        onTimeUpdate={onTime}
        onClick={toggle}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { if (typeof onEnded === "function") onEnded(); }}
        playsInline
        // Only set crossOrigin when captions (VTT) are present — a VTT track
        // requires CORS-enabled media. Leaving crossOrigin=anonymous on every
        // video breaks third-party sources that don't send CORS headers
        // (e.g. archive.org /download/ URLs for our public-domain classics).
        crossOrigin={vtt ? "anonymous" : undefined}
        x-webkit-airplay="allow"
      >
        {vtt && <track kind="subtitles" srcLang="en" label="English" src={vtt} default />}
      </video>

      {watermark && <Watermark variant={watermark} />}
      {watermark && <StreamWatermark variant={watermark} size="sm" />}

      {casting && (
        <div className="absolute top-3 right-3 bg-tv-brand text-black font-bold text-xs uppercase tracking-wider px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg" data-testid="casting-indicator">
          <Cast className="w-3.5 h-3.5" /> Casting to TV
        </div>
      )}

      {!playing && (
        <button
          onClick={toggle}
          data-testid="video-big-play"
          className="absolute inset-0 flex items-center justify-center"
        >
          <div className="w-20 h-20 rounded-full bg-tv-accent/95 text-black flex items-center justify-center shadow-2xl">
            <Play className="w-9 h-9 fill-current" />
          </div>
        </button>
      )}

      <div
        className={`absolute inset-x-0 bottom-0 p-3 sm:p-4 bg-gradient-to-t from-black/95 to-transparent transition-opacity ${hide && playing ? "opacity-0" : "opacity-100"}`}
      >
        <div
          className="tv-progress cursor-pointer mb-3"
          onClick={seek}
          data-testid="video-progress"
        >
          <div style={{ width: `${(progress / (duration || 1)) * 100}%` }} />
        </div>
        <div className="flex items-center gap-2 sm:gap-3 text-white">
          <button onClick={toggle} data-testid="video-play-btn" className="shrink-0">
            {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 fill-current" />}
          </button>
          <button
            data-testid="video-mute-btn"
            className="shrink-0"
            onClick={() => {
              const v = videoRef.current; if (!v) return;
              v.muted = !v.muted; setMuted(v.muted);
            }}
          >
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <div className="text-[11px] sm:text-xs text-white/85 tabular-nums">
            {fmt(progress)} / {fmt(duration)}
          </div>
          <div className="flex-1" />

          <button
            data-testid="video-cast-btn"
            onClick={castToTV}
            title={castAvailable ? "Cast to TV" : "Send to TV"}
            className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition ${
              casting ? "bg-tv-brand text-black" : castAvailable ? "bg-white/10 text-white hover:bg-white/20" : "bg-white/5 text-white/70 hover:bg-white/10"
            }`}
          >
            <Cast className="w-4 h-4" />
            <span className="hidden sm:inline">{casting ? "Casting" : "Cast"}</span>
          </button>

          {airplayAvailable && (
            <button
              data-testid="video-airplay-btn"
              onClick={airplayToTV}
              title="AirPlay"
              className="shrink-0 flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-2.5 py-1.5 rounded-full text-xs font-semibold"
            >
              <Airplay className="w-4 h-4" />
              <span className="hidden sm:inline">AirPlay</span>
            </button>
          )}

          <button data-testid="video-fullscreen-btn" onClick={full} className="shrink-0">
            <Maximize className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
